import * as ko from 'knockout';

import i18n from '../i18n';
import { originalUploadFileName, tryFormatDateTime } from '../utils';
import { MaybeKO } from '../utils/ko_utils';
import {
  ImageMetadata,
  MetadataTag,
  formatAltitude,
  formatDimensions,
  formatExposureTime,
  formatFNumber,
  formatFocalLength,
  readImageMetadata,
} from '../utils/image_metadata';
import { formatCoordinates, googleMapsSatelliteUrl } from '../utils/location';

let template = require('raw-loader!../../templates/components/image_metadata.html').default;

interface MetadataRow {
  label: string;
  value: string;
}

interface CaptureRow extends MetadataRow {
  /** The camera's UTC offset, or the fix's elevation — shown beside the value. */
  detail: string | null;
  href: string | null;
  /** Where the link goes, for the rows that are links. */
  hint: string | null;
}

interface MetadataView {
  capture: CaptureRow[];
  /** Exposure settings. */
  readouts: MetadataRow[];
  device: MetadataRow[];
  tags: MetadataTag[];
  tagsLabel: string;
}

function formatted<T>(value: T | undefined, format: (value: T) => string): string | undefined {
  return value === undefined ? undefined : format(value);
}

/** Keeps the candidates that have a value; the label literals stay visible to the localizer. */
function rows(...candidates: [string, string | undefined][]): MetadataRow[] {
  return candidates.flatMap(([label, value]) => (value === undefined ? [] : [{ label, value }]));
}

/**
 * The name the file was uploaded under, from the last segment of its URL — stored objects are keyed
 * by that name. Null for a photo captured in the app, which is stored under a bare UUID.
 */
function uploadedFileName(url: string): string | null {
  const storedName = url.split('?')[0].split('/').pop();
  return storedName ? originalUploadFileName(decodeURIComponent(storedName)) : null;
}

/** Null when the file carries nothing to show. */
function buildMetadataView(metadata: ImageMetadata): MetadataView | null {
  const capture: CaptureRow[] = [];
  if (metadata.dateTaken) {
    capture.push({
      label: i18n.t('Taken')(),
      value: tryFormatDateTime(metadata.dateTaken),
      detail: metadata.timeZoneOffset === undefined ? null : `UTC${metadata.timeZoneOffset}`,
      href: null,
      hint: null,
    });
  }
  if (metadata.gps) {
    capture.push({
      label: i18n.t('Location')(),
      value: formatCoordinates(metadata.gps.latitude, metadata.gps.longitude),
      detail: formatted(metadata.gps.altitude, formatAltitude) ?? null,
      href: googleMapsSatelliteUrl(metadata.gps.latitude, metadata.gps.longitude),
      hint: i18n.t('Open satellite view in Google Maps')(),
    });
  }
  const readouts = rows(
    [i18n.t('Shutter')(), formatted(metadata.exposureTime, formatExposureTime)],
    [i18n.t('Aperture')(), formatted(metadata.fNumber, formatFNumber)],
    [i18n.t('ISO')(), formatted(metadata.iso, String)],
    [i18n.t('Focal length')(), formatted(metadata.focalLength, formatFocalLength)],
    [i18n.t('35 mm equiv.')(), formatted(metadata.focalLength35mm, formatFocalLength)],
    [i18n.t('Flash')(), formatted(metadata.flashFired, (fired) => (fired ? i18n.t('On')() : i18n.t('Off')()))]
  );
  const device = rows(
    [i18n.t('Camera')(), metadata.camera],
    [i18n.t('Lens')(), metadata.lens],
    [i18n.t('Software')(), metadata.software],
    [i18n.t('Image size')(), formatted(metadata.dimensions, formatDimensions)]
  );
  const tags = metadata.tags;
  if (capture.length + readouts.length + device.length + tags.length === 0) {
    return null;
  }
  return {
    capture,
    readouts,
    device,
    tags,
    tagsLabel: i18n.t('All tags ({{ total }})', { total: tags.length })(),
  };
}

/**
 * The metadata embedded in an image file, read in the browser: images are uploaded straight to
 * cloud storage, so the server never sees their bytes. Never throws.
 */
async function readMetadataView(url: string, signal: AbortSignal): Promise<MetadataView | null> {
  try {
    // The <img> showing the picture keeps its plain src so it can never regress on a CORS failure.
    // This is a separate CORS request for the same URL and usually a second full download: the
    // bucket answers CORS requests with `Vary: Origin`, so the <img> response rarely serves it,
    // and its CORS rules allow GET but not Range, so the file cannot be read partially.
    const response = await fetch(url, { signal });
    if (!response.ok) {
      return null;
    }
    return buildMetadataView(await readImageMetadata(await response.blob()));
  } catch (error) {
    // Expected for files without EXIF (PNG, screenshots, photos re-encoded by the iOS app) and
    // for origins the bucket's CORS rules don't list — not an error worth reporting.
    return null;
  }
}

/** Whole originals are downloaded to read their EXIF, so only this many are in flight at once. */
const MAX_CONCURRENT_METADATA_READS = 2;
let activeReads = 0;
const waitingReads: (() => void)[] = [];

async function withReadSlot<T>(read: () => Promise<T>): Promise<T> {
  if (activeReads >= MAX_CONCURRENT_METADATA_READS) {
    await new Promise<void>((resolve) => waitingReads.push(resolve));
  }
  activeReads++;
  try {
    return await read();
  } finally {
    activeReads--;
    const next = waitingReads.shift();
    if (next) {
      next();
    }
  }
}

/** Shows the metadata embedded in the image at `url`; follows the URL when it is an observable. */
class ImageMetadataPanel {
  /** `undefined` while reading, `null` when the file has nothing to show, the view otherwise. */
  view = ko.observable<MetadataView | null | undefined>(undefined);

  /** Both are known before the image is read, so they show without waiting for the download. */
  collectedAt: string | null;
  fileName = ko.observable<string | null>(null);
  hasCaptureRows = ko.pureComputed(() => {
    const view = this.view();
    return !!view && view.capture.length > 0;
  });

  private abort = new AbortController();
  private urlSubscription: KnockoutSubscription | null = null;

  constructor(params: { url: MaybeKO<string>; collectedAt?: MaybeKO<string | null> }) {
    const collected = ko.unwrap(params.collectedAt);
    this.collectedAt = collected ? tryFormatDateTime(collected) : null;
    const url = params.url;
    if (ko.isObservable(url)) {
      this.urlSubscription = url.subscribe((value: string) => this.load(value));
    }
    this.load(ko.unwrap(url));
  }

  private async load(url: string) {
    this.fileName(uploadedFileName(url));
    this.abort.abort();
    this.abort = new AbortController();
    const signal = this.abort.signal;
    this.view(undefined);
    const view = url ? await withReadSlot(() => readMetadataView(url, signal)) : null;
    if (!signal.aborted) {
      this.view(view);
    }
  }

  dispose() {
    this.abort.abort();
    this.urlSubscription?.dispose();
  }
}

ko.components.register('image-metadata', { viewModel: ImageMetadataPanel, template: template });
